import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import type { AppDocument } from '../../types/document';

import {
  CreditCardIcon,
  CarIcon,
  HomeIcon,
  MedicalIcon,
  TaxIcon,
  BankIcon,
  LightBulbIcon,
  ShieldIcon,
  SubscriptionIcon,
  DocumentsIcon,
  ImportantDocumentsIcon,
  PhysicalAssetsIcon,
  CollectiblesAndValuablesIcon,
  ElectronicsIcon,
  NewDigitalAssetsIcon,
  CustomItemIcon,
} from '../ui/Icons';

interface Beneficiary {
  contactId: string;
  name: string;
}

interface CashAccount {
  id: string;
  type: 'checking' | 'savings' | 'money_market' | 'cd' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  routingNumber?: string;
  balance?: number;
  interestRate?: number;
  maturityDate?: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

interface InvestmentAccount {
  id: string;
  type: 'stocks' | 'bonds' | 'mutual_funds' | 'etfs' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  notes?: string;
  documents?: AppDocument[];
}

interface RetirementAccount {
  id: string;
  type: 'traditional_ira' | '401k' | 'roth_ira' | 'pension' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

interface OtherFinancialAsset {
  id: string;
  type: 'life_insurance' | 'annuity' | 'trust' | 'business' | 'other';
  name: string;
  institution?: string;
  policyNumber?: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

interface Property {
  id: string;
  type: 'house' | 'apartment' | 'land' | 'commercial' | 'other';
  address: string;
  mortgageBalance: number;
  notes?: string;
  displayName?: string;
  documents?: AppDocument[];
}

interface PersonalItem {
  id: string;
  category: 'physical_assets' | 'collectibles' | 'electronics' | 'digital_assets' | 'custom';
  name: string;
  description: string;
  estimatedValue?: number;
  accountNumber?: string;
  location: string;
  handlingInstructions: string;
  notes: string;
  createdAt: string;
  documents?: AppDocument[];
  assetType?: 'vehicle' | 'property' | 'title' | 'other';
  digitalType?: 'social_media' | 'email' | 'digital_media' | 'membership' | 'other';
  collectibleType?: 'art' | 'antique' | 'jewelry' | 'watch' | 'other';
}

interface IncomeSourceItem {
  id: string;
  type: 'social_security' | 'pension' | 'rental_income' | 'investment_income' | 'business_income' | 'annuity' | 'other';
  sourceName: string;
  payerInstitution: string;
  frequency: 'monthly' | 'quarterly' | 'annual';
  approximateAmount?: number;
  notes?: string;
  documents?: AppDocument[];
}

interface DebtItem {
  id: string;
  category: 'credit_card' | 'personal_auto' | 'mortgage' | 'medical' | 'tax' | 'other';
  creditor: string;
  amount?: number;
  accountNumber?: string;
  dueDate?: string;
  notes?: string;
  documents?: AppDocument[];
}

interface ExpenseItem {
  id: string;
  category: 'credit_card' | 'utilities' | 'insurance' | 'subscriptions' | 'transportation' | 'healthcare' | 'other';
  name: string;
  amount?: number;
  frequency: 'monthly' | 'quarterly' | 'annually' | 'unknown';
  dueDate?: string;
  accountNumber?: string;
  autopay: boolean;
  provider?: string;
  website?: string;
  notes?: string;
  documents?: AppDocument[];
}

type MajorCategoryKey =
  | 'banking'
  | 'investment'
  | 'retirement'
  | 'other_financial'
  | 'income_sources'
  | 'properties'
  | 'personal'
  | 'debts'
  | 'monthly_expenses';

type FilterKey = 'all' | MajorCategoryKey;

interface OverviewItem {
  id: string;
  categoryKey: MajorCategoryKey;
  categoryLabel: string;
  typeLabel: string;
  title: string;
  subtitle?: string;
  details: string[];
  notes?: string;
  documentsCount: number;
  raw: unknown;
}

const CATEGORY_LABELS: Record<MajorCategoryKey, string> = {
  banking: 'Banking',
  investment: 'Investment Accounts',
  retirement: 'Retirement Accounts',
  other_financial: 'Other Financial Assets',
  income_sources: 'Income Sources',
  properties: 'Properties',
  personal: 'Personal Belongings',
  debts: 'Debts & Liabilities',
  monthly_expenses: 'Monthly Expenses',
};

const MAJOR_CATEGORIES: { key: MajorCategoryKey; label: string }[] = [
  { key: 'banking', label: 'Banking' },
  { key: 'investment', label: 'Investment' },
  { key: 'retirement', label: 'Retirement' },
  { key: 'other_financial', label: 'Other Financial' },
  { key: 'income_sources', label: 'Income Sources' },
  { key: 'properties', label: 'Properties' },
  { key: 'personal', label: 'Personal Belongings' },
  { key: 'debts', label: 'Debts' },
  { key: 'monthly_expenses', label: 'Monthly Expenses' },
];

const BANKING_TYPES = [
  { value: 'checking', label: 'Checking Accounts' },
  { value: 'savings', label: 'Savings Accounts' },
  { value: 'money_market', label: 'Money Market Accounts' },
  { value: 'cd', label: 'Certificates of Deposit' },
  { value: 'other', label: 'Other Accounts' },
];

const INVESTMENT_TYPES = [
  { value: 'stocks', label: 'Stocks' },
  { value: 'bonds', label: 'Bonds' },
  { value: 'mutual_funds', label: 'Mutual Funds' },
  { value: 'etfs', label: 'ETFs' },
  { value: 'other', label: 'Other Investments' },
];

const RETIREMENT_TYPES = [
  { value: 'traditional_ira', label: 'Traditional IRA' },
  { value: '401k', label: '401(k)' },
  { value: 'roth_ira', label: 'Roth IRA' },
  { value: 'pension', label: 'Pension Plan' },
  { value: 'other', label: 'Other Retirement' },
];

const OTHER_FINANCIAL_TYPES = [
  { value: 'life_insurance', label: 'Life Insurance' },
  { value: 'annuity', label: 'Annuities' },
  { value: 'trust', label: 'Trust Funds' },
  { value: 'business', label: 'Business Interests' },
  { value: 'other', label: 'Other Assets' },
];

const PROPERTY_TYPES = [
  { value: 'house', label: 'House' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'land', label: 'Land' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'other', label: 'Other Property' },
];

const PERSONAL_TYPES = [
  { value: 'physical_assets', label: 'Physical Assets' },
  { value: 'collectibles', label: 'Collectibles & Valuables' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'digital_assets', label: 'Digital Assets' },
  { value: 'custom', label: 'Custom Item' },
];

const DEBT_TYPES = [
  { value: 'credit_card', label: 'Credit Card Debt' },
  { value: 'personal_auto', label: 'Personal & Auto Loans' },
  { value: 'mortgage', label: 'Property Mortgages' },
  { value: 'medical', label: 'Medical Expenses' },
  { value: 'tax', label: 'Tax Debt' },
  { value: 'other', label: 'Other Loans' },
];

const EXPENSE_TYPES = [
  { value: 'credit_card', label: 'Credit Card Payments' },
  { value: 'utilities', label: 'Utility Bills' },
  { value: 'insurance', label: 'Insurance Premiums' },
  { value: 'subscriptions', label: 'Recurring Subscriptions' },
  { value: 'transportation', label: 'Transportation Expenses' },
  { value: 'healthcare', label: 'Healthcare Expenses' },
  { value: 'other', label: 'Other Expenses' },
];

const INCOME_SOURCE_TYPES = [
  { value: 'social_security', label: 'Social Security' },
  { value: 'pension', label: 'Pension' },
  { value: 'rental_income', label: 'Rental Income' },
  { value: 'investment_income', label: 'Investment Income' },
  { value: 'business_income', label: 'Business Income' },
  { value: 'annuity', label: 'Annuity' },
  { value: 'other', label: 'Other Income' },
];

const CategoriesIcon = ({ className = 'w-6 h-6 text-cyan-700' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <rect x="3" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
  </svg>
);

const findLabel = (options: { value: string; label: string }[], value?: string) =>
  options.find((o) => o.value === value)?.label || value || '';

// Executor read-only: never fall back to browser localStorage backup keys.
const loadBackupArray = <T,>(_key: string): T[] => [];

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === "object")
    .map((doc) => ({
      id: typeof doc.id === "string" ? doc.id : "",
      name: typeof doc.name === "string" ? doc.name : "Untitled document",
      mimeType: typeof doc.mimeType === "string" ? doc.mimeType : undefined,
      size: typeof doc.size === "number" ? doc.size : undefined,
      createdAt: typeof doc.createdAt === "string" ? doc.createdAt : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const getAttachedDocuments = (item: OverviewItem): AppDocument[] => {
  switch (item.categoryKey) {
    case 'banking':
      return normalizeDocuments((item.raw as CashAccount).documents || []);
    case 'investment':
      return normalizeDocuments((item.raw as InvestmentAccount).documents || []);
    case 'retirement':
      return normalizeDocuments((item.raw as RetirementAccount).documents || []);
    case 'other_financial':
      return normalizeDocuments((item.raw as OtherFinancialAsset).documents || []);
    case 'income_sources':
      return normalizeDocuments((item.raw as IncomeSourceItem).documents || []);
    case 'properties':
      return normalizeDocuments((item.raw as Property).documents || []);
    case 'personal':
      return normalizeDocuments((item.raw as PersonalItem).documents || []);
    case 'debts':
      return normalizeDocuments((item.raw as DebtItem).documents || []);
    case 'monthly_expenses':
      return normalizeDocuments((item.raw as ExpenseItem).documents || []);
    default:
      return [];
  }
};

const getMajorCategoryIcon = (
  category: MajorCategoryKey,
  className = 'w-6 h-6 text-cyan-700'
) => {
  switch (category) {
    case 'banking':
      return <BankIcon className={className} />;
    case 'investment':
      return <LightBulbIcon className={className} />;
    case 'retirement':
      return <ShieldIcon className={className} />;
    case 'other_financial':
      return <ImportantDocumentsIcon className={className} />;
    case 'income_sources':
      return <BankIcon className={className} />;
    case 'properties':
      return <HomeIcon className={className} />;
    case 'personal':
      return <PhysicalAssetsIcon className={className} />;
    case 'debts':
      return <CreditCardIcon className={className} />;
    case 'monthly_expenses':
      return <SubscriptionIcon className={className} />;
    default:
      return <DocumentsIcon className={className} />;
  }
};

const getOverviewItemIcon = (
  item: OverviewItem,
  className = 'w-6 h-6 text-cyan-700'
) => {
  if (item.categoryKey === 'debts') {
    const debt = item.raw as DebtItem;
    switch (debt.category) {
      case 'credit_card':
        return <CreditCardIcon className={className} />;
      case 'personal_auto':
        return <CarIcon className={className} />;
      case 'mortgage':
        return <HomeIcon className={className} />;
      case 'medical':
        return <MedicalIcon className={className} />;
      case 'tax':
        return <TaxIcon className={className} />;
      default:
        return <DocumentsIcon className={className} />;
    }
  }

  if (item.categoryKey === 'monthly_expenses') {
    const expense = item.raw as ExpenseItem;
    switch (expense.category) {
      case 'credit_card':
        return <CreditCardIcon className={className} />;
      case 'insurance':
        return <ShieldIcon className={className} />;
      case 'subscriptions':
        return <SubscriptionIcon className={className} />;
      case 'transportation':
        return <CarIcon className={className} />;
      case 'healthcare':
        return <MedicalIcon className={className} />;
      default:
        return <DocumentsIcon className={className} />;
    }
  }

  if (item.categoryKey === 'personal') {
    const personal = item.raw as PersonalItem;
    switch (personal.category) {
      case 'physical_assets':
        return <PhysicalAssetsIcon className={className} />;
      case 'collectibles':
        return <CollectiblesAndValuablesIcon className={className} />;
      case 'electronics':
        return <ElectronicsIcon className={className} />;
      case 'digital_assets':
        return <NewDigitalAssetsIcon className={className} />;
      case 'custom':
        return <CustomItemIcon className={className} />;
      default:
        return <DocumentsIcon className={className} />;
    }
  }

  return getMajorCategoryIcon(item.categoryKey, className);
};

const EFrame143: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const estateOwnerName = getPlanOwnerName(session.name);

  const [loading, setLoading] = useState(true);
  const [cashAccounts, setCashAccounts] = useState<CashAccount[]>([]);
  const [investmentAccounts, setInvestmentAccounts] = useState<InvestmentAccount[]>([]);
  const [retirementAccounts, setRetirementAccounts] = useState<RetirementAccount[]>([]);
  const [otherFinancialAssets, setOtherFinancialAssets] = useState<OtherFinancialAsset[]>([]);
  const [incomeSources, setIncomeSources] = useState<IncomeSourceItem[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [personalItems, setPersonalItems] = useState<PersonalItem[]>([]);
  const [debts, setDebts] = useState<DebtItem[]>([]);
  const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<FilterKey>('all');

  useEffect(() => {
    const financialAnswers = session?.taskProgress?.financial_assets?.answers || {};
    const propertiesAnswers = session?.taskProgress?.properties?.answers || {};
    const personalAnswers = session?.taskProgress?.personal?.answers || {};
    const debtsAnswers = session?.taskProgress?.debts?.answers || {};
    const monthlyAnswers = session?.taskProgress?.monthly_expenses?.answers || {};

    setCashAccounts(
      (financialAnswers.cashAccounts as CashAccount[])?.length
        ? (financialAnswers.cashAccounts as CashAccount[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<CashAccount>('cash-accounts-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setInvestmentAccounts(
      (financialAnswers.investmentAccounts as InvestmentAccount[])?.length
        ? (financialAnswers.investmentAccounts as InvestmentAccount[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<InvestmentAccount>('investment-accounts-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setRetirementAccounts(
      (financialAnswers.retirementAccounts as RetirementAccount[])?.length
        ? (financialAnswers.retirementAccounts as RetirementAccount[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<RetirementAccount>('retirement-accounts-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setOtherFinancialAssets(
      (financialAnswers.otherFinancialAssets as OtherFinancialAsset[])?.length
        ? (financialAnswers.otherFinancialAssets as OtherFinancialAsset[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<OtherFinancialAsset>('other-financial-assets-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setIncomeSources(
      (financialAnswers.incomeSources as IncomeSourceItem[])?.length
        ? (financialAnswers.incomeSources as IncomeSourceItem[]).map((src) => ({
            ...src,
            documents: normalizeDocuments(src.documents || []),
          }))
        : loadBackupArray<IncomeSourceItem>('income-sources-backup').map((src) => ({
            ...src,
            documents: normalizeDocuments(src.documents || []),
          }))
    );

    setProperties(
      (propertiesAnswers.properties as Property[])?.length
        ? (propertiesAnswers.properties as Property[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<Property>('properties-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    const sessionPersonalItems =
      (personalAnswers.personalItems as Array<PersonalItem | { category?: string }>) || [];
    const backupPersonalItems =
      loadBackupArray<Array<PersonalItem | { category?: string }>[number]>('personal-items-backup');

    setPersonalItems(
      (sessionPersonalItems.length ? sessionPersonalItems : backupPersonalItems)
        .filter((item) => item.category !== 'documents')
        .map((item) => ({
          ...item,
          documents: normalizeDocuments((item as PersonalItem).documents || []),
        })) as PersonalItem[]
    );

    setDebts(
      ((debtsAnswers.debts as DebtItem[]) || []).map((item) => ({
        ...item,
        documents: normalizeDocuments(item.documents || []),
      }))
    );

    setExpenses(
      ((monthlyAnswers.expenses as ExpenseItem[]) || []).map((item) => ({
        ...item,
        documents: normalizeDocuments(item.documents || []),
      }))
    );

    setLoading(false);
  }, [
    session?.taskProgress?.financial_assets,
    session?.taskProgress?.properties,
    session?.taskProgress?.personal,
    session?.taskProgress?.debts,
    session?.taskProgress?.monthly_expenses,
  ]);

  const overviewItems: OverviewItem[] = useMemo(() => {
    const bankingItems: OverviewItem[] = cashAccounts.map((account) => ({
      id: account.id,
      categoryKey: 'banking',
      categoryLabel: CATEGORY_LABELS.banking,
      typeLabel: findLabel(BANKING_TYPES, account.type),
      title: account.name,
      subtitle: account.institution,
      details: [
        account.accountNumber ? `Account ending in ${account.accountNumber.slice(-4)}` : '',
        account.balance !== undefined ? `Balance: $${account.balance.toLocaleString()}` : '',
        account.interestRate !== undefined ? `Interest: ${account.interestRate}%` : '',
        account.maturityDate ? `Matures: ${account.maturityDate}` : '',
      ].filter(Boolean),
      notes: account.notes,
      documentsCount: account.documents?.length || 0,
      raw: account,
    }));

    const investmentItems: OverviewItem[] = investmentAccounts.map((account) => ({
      id: account.id,
      categoryKey: 'investment',
      categoryLabel: CATEGORY_LABELS.investment,
      typeLabel: findLabel(INVESTMENT_TYPES, account.type),
      title: account.name,
      subtitle: account.institution,
      details: [
        account.accountNumber ? `Account ending in ${account.accountNumber.slice(-4)}` : '',
      ].filter(Boolean),
      notes: account.notes,
      documentsCount: account.documents?.length || 0,
      raw: account,
    }));

    const retirementItems: OverviewItem[] = retirementAccounts.map((account) => ({
      id: account.id,
      categoryKey: 'retirement',
      categoryLabel: CATEGORY_LABELS.retirement,
      typeLabel: findLabel(RETIREMENT_TYPES, account.type),
      title: account.name,
      subtitle: account.institution,
      details: [
        account.accountNumber ? `Account ending in ${account.accountNumber.slice(-4)}` : '',
        account.beneficiaries?.length ? `Beneficiaries: ${account.beneficiaries.map((b) => b.name).join(', ')}` : '',
      ].filter(Boolean),
      notes: account.notes,
      documentsCount: account.documents?.length || 0,
      raw: account,
    }));

    const otherFinancialItems: OverviewItem[] = otherFinancialAssets.map((asset) => ({
      id: asset.id,
      categoryKey: 'other_financial',
      categoryLabel: CATEGORY_LABELS.other_financial,
      typeLabel: findLabel(OTHER_FINANCIAL_TYPES, asset.type),
      title: asset.name,
      subtitle: asset.institution || 'No provider listed',
      details: [
        asset.policyNumber ? `Policy / Account: ${asset.policyNumber}` : '',
        asset.beneficiaries?.length ? `Beneficiaries: ${asset.beneficiaries.map((b) => b.name).join(', ')}` : '',
      ].filter(Boolean),
      notes: asset.notes,
      documentsCount: asset.documents?.length || 0,
      raw: asset,
    }));

    const incomeSourceItems: OverviewItem[] = incomeSources.map((src) => ({
      id: src.id,
      categoryKey: 'income_sources',
      categoryLabel: CATEGORY_LABELS.income_sources,
      typeLabel: findLabel(INCOME_SOURCE_TYPES, src.type),
      title: src.sourceName,
      subtitle: src.payerInstitution,
      details: [
        src.frequency
          ? `Frequency: ${src.frequency.charAt(0).toUpperCase() + src.frequency.slice(1)}`
          : '',
        src.approximateAmount !== undefined
          ? `Approx. $${src.approximateAmount.toLocaleString()}`
          : '',
      ].filter(Boolean),
      notes: src.notes,
      documentsCount: src.documents?.length || 0,
      raw: src,
    }));

    const propertyItems: OverviewItem[] = properties.map((property) => ({
      id: property.id,
      categoryKey: 'properties',
      categoryLabel: CATEGORY_LABELS.properties,
      typeLabel: findLabel(PROPERTY_TYPES, property.type),
      title: property.displayName || property.address,
      subtitle: property.address,
      details: [`Mortgage Balance: $${(property.mortgageBalance || 0).toLocaleString()}`],
      notes: property.notes,
      documentsCount: property.documents?.length || 0,
      raw: property,
    }));

    const personalOverview: OverviewItem[] = personalItems
      .filter((item: unknown) => item.category !== 'documents')
      .map((item) => {
        const subType = item.assetType || item.digitalType || item.collectibleType || '';
        return {
          id: item.id,
          categoryKey: 'personal',
          categoryLabel: CATEGORY_LABELS.personal,
          typeLabel: `${findLabel(PERSONAL_TYPES, item.category)}${subType ? ` • ${subType.replace(/_/g, ' ')}` : ''}`,
          title: item.name,
          subtitle: item.location || 'No location listed',
          details: [
            item.estimatedValue !== undefined ? `Value: $${item.estimatedValue.toLocaleString()}` : '',
            item.accountNumber ? `Account / ID: ${item.accountNumber}` : '',
            item.handlingInstructions ? `Handling: ${item.handlingInstructions}` : '',
          ].filter(Boolean),
          notes: item.notes || item.description,
          documentsCount: item.documents?.length || 0,
          raw: item,
        };
      });

    const debtItems: OverviewItem[] = debts.map((debt) => ({
      id: debt.id,
      categoryKey: 'debts',
      categoryLabel: CATEGORY_LABELS.debts,
      typeLabel: findLabel(DEBT_TYPES, debt.category),
      title: debt.creditor,
      subtitle: 'Debt / Liability',
      details: [
        debt.amount !== undefined ? `Amount: $${debt.amount.toLocaleString()}` : '',
        debt.accountNumber ? `Account ending in ${debt.accountNumber.slice(-4)}` : '',
        debt.dueDate ? `Due: ${new Date(debt.dueDate).toLocaleDateString()}` : '',
      ].filter(Boolean),
      notes: debt.notes,
      documentsCount: debt.documents?.length || 0,
      raw: debt,
    }));

    const expenseItems: OverviewItem[] = expenses.map((expense) => ({
      id: expense.id,
      categoryKey: 'monthly_expenses',
      categoryLabel: CATEGORY_LABELS.monthly_expenses,
      typeLabel: findLabel(EXPENSE_TYPES, expense.category),
      title: expense.name,
      subtitle: expense.provider || 'Recurring expense',
      details: [
        expense.amount !== undefined ? `$${expense.amount.toLocaleString()} / ${expense.frequency}` : expense.frequency,
        expense.accountNumber ? `Account ending in ${expense.accountNumber.slice(-4)}` : '',
        expense.dueDate ? `Due: ${new Date(expense.dueDate).toLocaleDateString()}` : '',
        expense.autopay ? 'AutoPay Enabled' : '',
      ].filter(Boolean),
      notes: expense.notes,
      documentsCount: expense.documents?.length || 0,
      raw: expense,
    }));

    return [
      ...bankingItems,
      ...investmentItems,
      ...retirementItems,
      ...otherFinancialItems,
      ...incomeSourceItems,
      ...propertyItems,
      ...personalOverview,
      ...debtItems,
      ...expenseItems,
    ];
  }, [
    cashAccounts,
    investmentAccounts,
    retirementAccounts,
    otherFinancialAssets,
    incomeSources,
    properties,
    personalItems,
    debts,
    expenses,
  ]);

  const filteredItems = useMemo(() => {
    if (selectedFilter === 'all') return overviewItems;
    return overviewItems.filter((item) => item.categoryKey === selectedFilter);
  }, [overviewItems, selectedFilter]);

  const categoryCounts = useMemo(() => ({
    banking: cashAccounts.length,
    investment: investmentAccounts.length,
    retirement: retirementAccounts.length,
    other_financial: otherFinancialAssets.length,
    income_sources: incomeSources.length,
    properties: properties.length,
    personal: personalItems.length,
    debts: debts.length,
    monthly_expenses: expenses.length,
  }), [
    cashAccounts.length,
    investmentAccounts.length,
    retirementAccounts.length,
    otherFinancialAssets.length,
    incomeSources.length,
    properties.length,
    personalItems.length,
    debts.length,
    expenses.length,
  ]);

  const totalAccounts = overviewItems.length;
  const totalCategories = Object.values(categoryCounts).filter((count) => count > 0).length;

  const statCards = [
    {
      label: 'Total Accounts',
      value: totalAccounts,
      icon: <BankIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Categories',
      value: totalCategories,
      icon: <CategoriesIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px] min-w-0 overflow-x-hidden">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <ResponsiveHeader title={`${estateOwnerName}’s Accounts & Assets`} />
            </div>
          </div>

          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed break-words">
            View the financial and physical assets associated with {estateOwnerName}’s estate—from bank accounts and investments to property and valuables.
          </p>

          <p className="mt-3 text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed break-words">
            Understanding what exists helps you secure, manage, and prepare for distribution.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {statCards.map((card) => (
              <div key={card.label} className="bg-white rounded-md border border-stone-200 p-6">
                <div className="flex items-start gap-3">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col">
                    <div className="font-sans text-base text-stone-600">{card.label}</div>
                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4">
                      {loading ? '—' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
              <h2 className="font-sans text-xl font-medium text-stone-800">
                All Accounts & Assets
              </h2>

              <div className="shrink-0">
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value as FilterKey)}
                  className="min-w-[220px] px-3 py-2 border border-stone-300 rounded-md bg-white text-sm text-stone-800"
                >
                  <option value="all">All ({totalAccounts})</option>
                  {MAJOR_CATEGORIES.map((category) => (
                    <option key={category.key} value={category.key}>
                      {category.label} ({categoryCounts[category.key]})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading accounts and assets...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No items found for this filter yet.
                  </p>
                </div>
              ) : (
                filteredItems.map((item) => {
                  const attachedDocuments = getAttachedDocuments(item);

                  return (
                    <div
                      key={`${item.categoryKey}-${item.id}`}
                      className="flex flex-col sm:flex-row sm:items-start gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 min-w-0 overflow-hidden"
                    >
                      <div className="shrink-0 mt-0.5">
                        {getOverviewItemIcon(item, 'w-6 h-6 text-cyan-700')}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 min-w-0">
                          <div className="font-sans text-lg font-medium text-stone-800 break-words min-w-0">
                            {item.title}
                          </div>
                          <span className="inline-flex items-center rounded-full bg-stone-200 text-stone-800 px-2.5 py-1 text-xs font-medium">
                            {item.categoryLabel}
                          </span>
                        </div>

                        <div className="font-sans text-base text-stone-600 mt-1 break-words">
                          {item.typeLabel}
                        </div>

                        {item.subtitle && (
                          <div className="font-sans text-base text-stone-600 mt-1 break-words">
                            {item.subtitle}
                          </div>
                        )}

                        {item.details.length > 0 && (
                          <div className="flex flex-wrap gap-x-5 gap-y-1 font-sans text-base text-stone-600 mt-1">
                            {item.details.map((detail, index) => (
                              <span key={index} className="break-words">
                                {detail}
                              </span>
                            ))}
                          </div>
                        )}

                        {item.notes && (
                          <div className="font-sans text-sm text-stone-500 mt-2 break-words">
                            {item.notes}
                          </div>
                        )}

                        {attachedDocuments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <div className="font-sans text-sm text-cyan-700">
                              {attachedDocuments.length} document(s) attached
                            </div>

                            <div className="flex flex-col gap-2">
                              {attachedDocuments.map((doc, index) => (
                                <div
                                  key={`${doc.id}-${index}`}
                                  className="flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50 px-3 py-2"
                                >
                                  {getMajorCategoryIcon(
                                    item.categoryKey,
                                    "w-4 h-4 text-cyan-700 shrink-0 mt-0.5"
                                  )}

                                  <span
                                    className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
                                    title={doc.name}
                                  >
                                    {doc.name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame143;